import {
  extractErrorMessage,
  reportTicketCostLoadError,
  type TicketCostDetailsTransport,
  type TicketCostInvoiceTransport,
  useGetTicketCostDetailsQuery,
} from "@ots-frontend/ots-data";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo } from "react";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import { mapTicketCostInvoices, mapTicketCostRateModels } from "../ticket-cost-mapper";
import { TicketCostDialog } from "../TicketCostDialog";

export type ConnectedTicketCostDialogProps = {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  database?: string;
  title?: string;
  description?: string;
  /** Costs already loaded with the ticket, shown while (or if) the latest versions cannot be fetched. */
  initialDetails?: TicketCostDetailsTransport;
  invoices?: TicketCostInvoiceTransport[];
  /** "Total cost", or "Total revenue" for a trucking company. */
  financialLabel?: string;
  /** Opens the preview version first, e.g. for people who may not see posted costs. */
  preferPreview?: boolean;
  onViewAllocation?: () => void;
  onOpenLegacy?: () => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** The ticket cost dialog with its versions fetched (`useGetTicketCostDetailsQuery`). Shared by WaterGo and DispatchGo. */
export const ConnectedTicketCostDialog = ({
  open,
  onClose,
  ticketId,
  database,
  title,
  description,
  initialDetails,
  invoices: invoiceTransports,
  financialLabel = "Total cost",
  preferPreview = false,
  onViewAllocation,
  onOpenLegacy,
  brand = "water-go",
  colorScheme = "system",
}: ConnectedTicketCostDialogProps) => {
  const { data, error, isFetching, refetch } = useGetTicketCostDetailsQuery(open ? { ticketId, database } : skipToken);
  useEffect(() => {
    if (error) reportTicketCostLoadError("query", error);
  }, [error]);

  const invoices = useMemo(() => mapTicketCostInvoices(invoiceTransports ?? []), [invoiceTransports]);
  const displayedDetails = data ?? initialDetails;
  const rateModels = useMemo(
    () => displayedDetails ? mapTicketCostRateModels(displayedDetails, invoices, financialLabel) : [],
    [displayedDetails, financialLabel, invoices],
  );
  const shouldPreferPreview = preferPreview
    || (!rateModels.some((model) => model.versionKind === "current") && rateModels.some((model) => model.versionKind === "preview"));

  return (
    <TicketCostDialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      rateModels={rateModels}
      initialVersionKind={shouldPreferPreview ? "preview" : "current"}
      loading={isFetching && !displayedDetails}
      refreshing={isFetching && Boolean(displayedDetails)}
      error={error && !displayedDetails ? extractErrorMessage(error, "The ticket costs could not be loaded.") : undefined}
      notice={[
        data?.previewError ? `${data.previewError} Posted and historical costs are still shown.` : undefined,
        data?.billingLocationError,
        error && initialDetails ? "The latest cost versions could not be loaded. Showing costs already loaded with the ticket." : undefined,
      ].filter(Boolean).join(" ") || undefined}
      onRetry={() => { void refetch(); }}
      onViewAllocation={onViewAllocation}
      onOpenLegacy={onOpenLegacy}
      brand={brand}
      colorScheme={colorScheme}/>
  );
};
